import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, User } from 'lucide-react';
import BackBar from '../components/BackBar';
import Footer from '../components/Footer';
import RegisterGate from '../components/RegisterGate';
import PhoneInput from '../components/PhoneInput';
import { updateMe, getMe } from '../api/portal';
import './ApplyPage.css';

// Owner correction (2026-07-25): landing an incomplete account on the dashboard's own "Finish your
// registration" gate is a dead end reached only by remembering to click through it — logging in should
// go straight to the one actual next step. Either way /apply is the only thing left to do, and its own
// mount effect already resumes at the right step for an existing session.
async function routeAfterLogin(navigate) {
  try {
    const me = await getMe();
    navigate(me.hasApplication ? '/dashboard' : '/apply');
  } catch {
    navigate('/dashboard'); // network hiccup — dashboard's own gate is a safe fallback either way
  }
}

// OTP (one-time email code) is the ONLY sign-in path (candidate-apply-rework, 2026-07-26 — password
// sign-in is no longer surfaced; the backend route stays but is never called here). Signing in with a
// code for a brand-new email self-vivifies an account, so a genuinely new visitor gets the name/phone
// interstitial below before continuing on.
export default function LoginPage() {
  const navigate = useNavigate();

  // `pendingAccount` holds a just-verified brand-new account while we collect a name (mandatory) and
  // phone (optional) — the OTP gate itself asks for email only. `hadExistingName` (returned by
  // RegisterGate) distinguishes a returning account (route straight on) from a fresh one (collect first).
  const [pendingAccount, setPendingAccount] = useState(null);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [interstitialBusy, setInterstitialBusy] = useState(false);

  // Single-active-session: portal.js redirects here with ?loggedout=1 when this session was signed out
  // by a newer login on another device. Show a one-line notice so the bounce isn't mysterious.
  const wasSignedOutElsewhere = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('loggedout') === '1';

  async function handleOtpVerified(acc) {
    if (!acc.hadExistingName) {
      setPendingAccount(acc);
      return;
    }
    await routeAfterLogin(navigate);
  }

  async function handleInterstitialContinue(e) {
    e?.preventDefault();
    if (!fullName.trim() || interstitialBusy) return;
    setInterstitialBusy(true);
    try {
      // Same first-space split convention as RegisterGate / DashboardPage — the backend stores
      // firstName/lastName as two columns even though this is one visible field.
      const trimmed = fullName.trim().replace(/\s+/g, ' ');
      const spaceAt = trimmed.indexOf(' ');
      const payload = {
        firstName: spaceAt === -1 ? trimmed : trimmed.slice(0, spaceAt),
        lastName: spaceAt === -1 ? '' : trimmed.slice(spaceAt + 1),
      };
      if (phone.trim()) payload.phone = phone.trim();
      await updateMe(payload);
    } catch { /* non-fatal — best-effort; never strand the candidate on a save failure here */ }
    await routeAfterLogin(navigate);
  }

  return (
    <>
      <BackBar variant="task" backTo="/" backLabel="All jobs" />
      <main className="apply-main container">
        <div className="apply-card">
          {wasSignedOutElsewhere && !pendingAccount ? (
            <div role="status" style={{
              marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: '0.6rem',
              background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.35)',
              color: '#1d4ed8', fontSize: '0.9rem', lineHeight: 1.4,
            }}>
              You were signed out because your account was used to sign in on another device. Please sign in again.
            </div>
          ) : null}
          {pendingAccount ? (
            <form className="apply-step" onSubmit={handleInterstitialContinue}>
              <h2 className="authgate-title">One more thing</h2>
              <label className="apply-label" htmlFor="login-name"><User size={18} /> Full name</label>
              <input id="login-name" type="text" autoComplete="name" autoFocus
                className="apply-input" placeholder="As on your passport"
                value={fullName} onChange={(e) => setFullName(e.target.value.toUpperCase())} />
              <label className="apply-label" htmlFor="login-phone" style={{ marginTop: '1rem' }}>
                Phone number <span className="apply-optional">(optional)</span>
              </label>
              <PhoneInput id="login-phone" value={phone} onChange={setPhone} />
              <button type="submit" className="apply-btn" disabled={!fullName.trim() || interstitialBusy}>
                {interstitialBusy ? <Loader2 className="spin" size={18} /> : null} Continue
              </button>
            </form>
          ) : (
            // Email-only OTP sign-in. Reuses RegisterGate for its CAPTCHA gate (no captcha-less OTP path
            // exists in this app). A brand-new account gets the name/phone interstitial above instead of
            // routing immediately.
            <RegisterGate title="Sign in with a code" showName={false} showPhone={false} onVerified={handleOtpVerified} />
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
